import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, LogOut, Building2, Users, FileText, Activity, Trash2, RefreshCw, X } from "lucide-react";

const PLAN_LABEL = {
  sandbox: "Sandbox",
  trial: "Trial",
  active: "Activo",
  suspended: "Suspendido",
};

const PLAN_COLOR = {
  sandbox: "bg-amber-100 text-amber-800 border-amber-300",
  trial: "bg-blue-100 text-blue-800 border-blue-300",
  active: "bg-emerald-100 text-emerald-800 border-emerald-300",
  suspended: "bg-zinc-200 text-zinc-700 border-zinc-300",
};

export default function SuperAdmin() {
  const { user, logout } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    nombre: "",
    slug: "",
    contacto_email: "",
    contacto_nombre: "",
    plan: "trial",
    admin_email: "",
    admin_password: "",
    admin_nombre: "",
  });

  const load = () => {
    setLoading(true);
    api
      .get("/superadmin/overview")
      .then((r) => setOverview(r.data))
      .catch(() => toast.error("Error al cargar"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/superadmin/tenants", form);
      toast.success("Universidad creada");
      setShowForm(false);
      setForm({
        nombre: "",
        slug: "",
        contacto_email: "",
        contacto_nombre: "",
        plan: "trial",
        admin_email: "",
        admin_password: "",
        admin_nombre: "",
      });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear universidad");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (t) => {
    if (t.slug === "demo") {
      toast.error("No se puede eliminar la universidad demo");
      return;
    }
    if (!window.confirm(`¿Eliminar "${t.nombre}" y TODOS sus datos? Esta acción es irreversible.`)) return;
    try {
      await api.delete(`/superadmin/tenants/${t.id}`);
      toast.success("Universidad eliminada");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    }
  };

  const resetDemo = async () => {
    if (!window.confirm("¿Resetear todos los datos del sandbox demo? Se borrarán cambios de visitantes y se recreará desde cero.")) return;
    try {
      await api.post("/superadmin/seed-demo-reset");
      toast.success("Sandbox reseteado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al resetear");
    }
  };

  return (
    <div className="min-h-screen bg-white text-zinc-950">
      {/* Top bar */}
      <header className="border-b-2 border-zinc-950 bg-white sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <Link to="/" className="flex items-baseline gap-1">
              <span className="text-2xl font-semibold tracking-tighter">vincularedu</span>
              <span className="text-2xl font-medium text-zinc-400">.</span>
            </Link>
            <span className="hidden sm:inline-block ml-2 text-[10px] uppercase tracking-widest font-bold bg-zinc-950 text-white px-2 py-1">
              Super-admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-zinc-500 hidden sm:inline">{user?.email}</span>
            <button
              onClick={logout}
              className="text-sm text-zinc-600 hover:text-zinc-950 inline-flex items-center gap-1.5"
              data-testid="superadmin-logout"
            >
              <LogOut size={14} strokeWidth={1.5} /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
          <div>
            <div className="section-marker">Plataforma · Vista global</div>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter mt-2">
              Universidades<span className="serif-em text-zinc-900">.</span>
            </h1>
            <p className="mt-3 text-zinc-600 max-w-2xl">
              Vista de monitoreo de todos los clientes en la plataforma.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              onClick={resetDemo}
              className="border border-zinc-300 px-4 py-2.5 text-sm hover:bg-zinc-50 inline-flex items-center justify-center gap-2"
              data-testid="btn-reset-demo"
            >
              <RefreshCw size={14} strokeWidth={1.5} /> Resetear sandbox
            </button>
            <button
              onClick={() => setShowForm(true)}
              className="bg-zinc-950 text-white px-5 py-3 font-medium hover:bg-zinc-800 inline-flex items-center justify-center gap-2 text-sm sm:text-base"
              data-testid="btn-new-tenant"
            >
              <Plus size={18} strokeWidth={1.5} /> Nueva universidad
            </button>
          </div>
        </div>

        {/* KPIs globales */}
        {overview && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8 sm:mb-10 pb-8 sm:pb-10 border-b border-zinc-200" data-testid="global-kpis">
            <div>
              <div className="section-marker flex items-center gap-1"><Building2 size={12} strokeWidth={2} /> Tenants</div>
              <div className="kpi-number text-4xl sm:text-6xl mt-2">{overview.total_tenants}</div>
            </div>
            <div>
              <div className="section-marker flex items-center gap-1"><Users size={12} strokeWidth={2} /> Usuarios</div>
              <div className="kpi-number text-4xl sm:text-6xl mt-2">{overview.total_users}</div>
            </div>
            <div>
              <div className="section-marker flex items-center gap-1"><FileText size={12} strokeWidth={2} /> Convenios</div>
              <div className="kpi-number text-4xl sm:text-6xl mt-2">{overview.total_convenios}</div>
            </div>
            <div>
              <div className="section-marker flex items-center gap-1"><Activity size={12} strokeWidth={2} /> Acciones</div>
              <div className="kpi-number text-4xl sm:text-6xl mt-2">{overview.total_acciones}</div>
            </div>
          </div>
        )}

        {/* Tenants table */}
        {loading ? (
          <div className="text-zinc-500 py-8">Cargando...</div>
        ) : (
          <div className="border-t-2 border-zinc-950 overflow-x-auto">
            <table className="w-full min-w-[800px]" data-testid="tenants-table">
              <thead>
                <tr>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">Universidad</th>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">Slug</th>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">Plan</th>
                  <th className="text-left py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">Contacto</th>
                  <th className="text-right py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">Usuarios</th>
                  <th className="text-right py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">Convenios</th>
                  <th className="text-right py-3 text-xs font-bold uppercase tracking-wider text-zinc-500">Acciones</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {overview?.tenants?.length === 0 && (
                  <tr><td colSpan={8} className="py-12 text-center text-zinc-500">Sin universidades</td></tr>
                )}
                {overview?.tenants?.map((t) => (
                  <tr key={t.id} className="border-t border-zinc-100" data-testid={`tenant-row-${t.slug}`}>
                    <td className="py-4 font-medium">{t.nombre}</td>
                    <td className="py-4 text-zinc-600 font-mono text-xs">{t.slug}</td>
                    <td className="py-4">
                      <span className={`inline-block px-2 py-0.5 text-xs border ${PLAN_COLOR[t.plan] || "bg-zinc-100 text-zinc-700 border-zinc-300"}`}>
                        {PLAN_LABEL[t.plan] || t.plan}
                      </span>
                    </td>
                    <td className="py-4 text-zinc-600 text-sm">{t.contacto_email}</td>
                    <td className="py-4 text-right tabular-nums">{t.users}</td>
                    <td className="py-4 text-right tabular-nums">{t.convenios}</td>
                    <td className="py-4 text-right tabular-nums">{t.acciones}</td>
                    <td className="py-4 text-right">
                      {t.slug !== "demo" && (
                        <button
                          onClick={() => remove(t)}
                          className="text-zinc-400 hover:text-red-700 p-1"
                          aria-label="Eliminar"
                          data-testid={`tenant-delete-${t.slug}`}
                        >
                          <Trash2 size={16} strokeWidth={1.5} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Modal: Nueva universidad */}
      {showForm && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setShowForm(false)}
        >
          <div
            className="bg-white w-full max-w-2xl p-6 sm:p-8 border border-zinc-200 max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="tenant-modal"
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="section-marker">Crear</div>
                <h2 className="text-2xl sm:text-3xl font-medium tracking-tight mt-1">Nueva universidad</h2>
              </div>
              <button onClick={() => setShowForm(false)} className="text-zinc-400 hover:text-zinc-950 p-1">
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            <form onSubmit={create} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Nombre</label>
                  <input required value={form.nombre} onChange={set("nombre")} className="w-full border border-zinc-300 px-4 py-3" data-testid="t-nombre" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Slug (identificador)</label>
                  <input required value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))} placeholder="ej: uchile" className="w-full border border-zinc-300 px-4 py-3 font-mono" data-testid="t-slug" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Email contacto</label>
                  <input required type="email" value={form.contacto_email} onChange={set("contacto_email")} className="w-full border border-zinc-300 px-4 py-3" data-testid="t-email" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Nombre contacto</label>
                  <input value={form.contacto_nombre} onChange={set("contacto_nombre")} className="w-full border border-zinc-300 px-4 py-3" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Plan</label>
                <select value={form.plan} onChange={set("plan")} className="w-full border border-zinc-300 px-4 py-3 bg-white" data-testid="t-plan">
                  <option value="trial">Trial</option>
                  <option value="active">Activo</option>
                  <option value="suspended">Suspendido</option>
                </select>
              </div>

              <div className="pt-4 border-t border-zinc-200">
                <div className="section-marker mb-3">Admin inicial (opcional)</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Nombre</label>
                    <input value={form.admin_nombre} onChange={set("admin_nombre")} className="w-full border border-zinc-300 px-4 py-3" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Email</label>
                    <input type="email" value={form.admin_email} onChange={set("admin_email")} className="w-full border border-zinc-300 px-4 py-3" data-testid="t-admin-email" />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Contraseña</label>
                  <input type="text" value={form.admin_password} onChange={set("admin_password")} placeholder="Mínimo 6 caracteres" className="w-full border border-zinc-300 px-4 py-3 font-mono" data-testid="t-admin-pwd" />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button type="button" onClick={() => setShowForm(false)} className="border border-zinc-300 px-6 py-3 font-medium hover:bg-zinc-50">
                  Cancelar
                </button>
                <button type="submit" disabled={creating} className="flex-1 bg-zinc-950 text-white px-6 py-3 font-medium hover:bg-zinc-800 disabled:opacity-50" data-testid="t-submit">
                  {creating ? "Creando..." : "Crear universidad"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
